import React, { useEffect, useRef, useState } from "react";
import {
  Download,
  Clipboard2Data,
  CheckCircle,
  Envelope,
  Send,
  FileEarmarkSpreadsheet,
} from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { useRole } from "hooks/useRole";
import {
  ERROR_MESSAGE,
  SUCCESS_MESSAGE,
  UserRole,
} from "components/Reports/reportAssets/assets";
import { useLocationForReportType } from "hooks/useLocationForReportType";
import { downloadExcelFile, downloadZipFolder } from "utils/downloadFiles";
import { baseUrl } from "features/api/fetchBaseQuery";
import {
  useSendToPoliceMutation,
  useUpdatePoliceDecisionMutation,
  useGetProcessStatusQuery,
} from "features/api/reports/reportsSlice";
import ModalBootstrap from "components/bootstrap/ModalBootstrap";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import { expandedFiltersType, filtersType } from "pages/ReportsPage/types";
import { ControlCenterProcesses } from "models/ControlCenterProcess";
import "../../pages/PhoneLogin/components/PhoneVerification";
import MassLoading from "./components/MassLoading";

type Props = {
  title: string;
  num: string | number;
  checkedReportsIds: number[];
  bodyToExportExcel: {
    expandedReportsFilters: expandedFiltersType;
    reportsFilters: filtersType;
    searchInput: string;
  };
  onReportRefetch: () => void;
};

type policeDecisionCounts = {
  disqualified: { count: number; text: string };
  report: { count: number; text: string };
  summonsToCourt: { count: number; text: string };
};

const Title = ({
  title,
  num,
  checkedReportsIds,
  bodyToExportExcel,
  onReportRefetch,
}: Props) => {
  const { t } = useTranslation();
  const role = useRole();
  const isFollowUp = useLocationForReportType() === "followup";
  const fileInput = useRef(null);
  const [modalShow, setModalShow] = useState(false);
  const [modalMassLoadingShow, setModalMassLoadingShow] = useState(false);
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const [isLoadingExportExcel, setIsLoadingExportExcel] = useState(false);
  const [isLoadingUploadFile, setIsLoadingUploadFile] = useState(false);
  const [isLoadingSendToPolice, setIsLoadingSendToPolice] = useState(false);
  const [isSendToPoliceBlocked, setIsSendToPoliceBlocked] = useState(false);
  const [
    isLoadingExportInformationLetter,
    setIsLoadingExportInformationLetter,
  ] = useState(false);
  const [responseData, setResponseData] = useState<policeDecisionCounts>({
    disqualified: { count: null, text: t("policeDecisionRejectedReports") },
    report: { count: null, text: t("policeDecisionReportsToReport") },
    summonsToCourt: { count: null, text: t("policeDecisionCourtSummons") },
  });

  const [updatePoliceDecision] = useUpdatePoliceDecisionMutation();
  const [sendToPolice] = useSendToPoliceMutation();
  const {
    data: controlCenterProcessStatuses,
  }: { data: ControlCenterProcesses } = useGetProcessStatusQuery(
    {},
    {
      skip: role !== UserRole.CONTROLCENTER,
    }
  );

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  useEffect(() => {
    if (controlCenterProcessStatuses) {
      setIsSendToPoliceBlocked(
        controlCenterProcessStatuses.sendToPoliceProcess
      );
    }
  }, [controlCenterProcessStatuses]);

  const handleDownloadCheckedReports = (isChecked: boolean) => {
    const body = isChecked
      ? { selectedReportsIds: checkedReportsIds }
      : bodyToExportExcel;
    downloadExcelFile(
      `${baseUrl}/controlcenter/GetReoportsListAsExelFile`,
      JSON.stringify(body),
      "Reports.xlsx",
      isChecked ? setIsLoadingDownload : setIsLoadingExportExcel
    );
  };

  const handleExportInformationLetterReports = async () => {
    try {
      downloadZipFolder(
        `${baseUrl}/controlcenter/exportNotificationLetter`,
        "Notification Letters.zip",
        setIsLoadingExportInformationLetter,
        onReportRefetch
      );
    } catch (error) {
      console.error("Error:", error);
      toast.error(ERROR_MESSAGE);
    }
  };

  const handleSendToPoliceReports = async () => {
    try {
      setIsLoadingSendToPolice(true);
      const res = await sendToPolice().unwrap(); // TODO: wait to server updates to check if work
      setIsLoadingSendToPolice(false);
      if (res) {
        toast.success(SUCCESS_MESSAGE);
      }
    } catch (error) {
      setIsLoadingSendToPolice(false);
      console.error("Error:", error);
      toast.error(ERROR_MESSAGE);
    }
  };

  const handleConfirmSendToPolice = () => {
    setShowConfirmationModal(false);
    handleSendToPoliceReports();
  };

  const handleUploadFile = async (event) => {
    setIsLoadingUploadFile(true);
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("exelFile", file);

      try {
        const res = await updatePoliceDecision(formData).unwrap();

        const updatedData = Object.keys(res).reduce(
          (result, key) => {
            const copyResult = result;
            if (responseData[key]) {
              copyResult[key] = { ...responseData[key], count: res[key] };
            }
            return result;
          },
          { ...responseData }
        );

        setIsLoadingUploadFile(false);
        setResponseData(updatedData);
        setModalShow(true);
        fileInput.current.value = null; // reset the input to allow another request with same file (refers to reduxTK - cache)
      } catch (error) {
        console.error("Error:", error);
        toast.error(ERROR_MESSAGE);
      }
    }
  };

  const getModalChildren = () => {
    const totalReports = Object.values(responseData).reduce(
      (accumulator, currentValue) => accumulator + currentValue.count,
      0
    );

    return (
      <div>
        <div>{`${t("feedbackReversedReports", { count: totalReports })} ${
          totalReports > 0 ? t("accordingToPoliceDecision") : ""
        }`}</div>
        <ul className="policeDecisionList">
          {Object.values(responseData).map(
            ({ count, text }) =>
              count !== 0 && (
                <li key={text}>
                  <div>{`${count} ${text}`}</div>
                </li>
              )
          )}
        </ul>
      </div>
    );
  };
  return (
    <div className="titleContainer">
      <div className="titleBox">
        <div className="title">{title}</div>
        <div className="num">({num})</div>
      </div>
      {role === UserRole.CONTROLCENTER &&
        (isFollowUp ? (
          <div>
            {isLoadingDownload ? (
              <Spinner animation="border" variant="secondary" />
            ) : (
              <button
                type="button"
                className={`downloadButton ${
                  checkedReportsIds.length === 0 ? "disabled" : ""
                }`}
                disabled={checkedReportsIds.length === 0}
                onClick={() => handleDownloadCheckedReports(true)}
              >
                <Download size={24} />
                {t("download")}
              </button>
            )}

            {isLoadingExportExcel ? (
              <Spinner animation="border" variant="secondary" />
            ) : (
              <button
                type="button"
                className="downloadButton"
                onClick={() => handleDownloadCheckedReports(false)}
              >
                <Clipboard2Data size={24} />
                {t("exportReport")}
              </button>
            )}
            <button
              type="button"
              className="downloadButton"
              onClick={() => fileInput.current.click()}
              disabled={isLoadingUploadFile}
            >
              {isLoadingUploadFile ? (
                <Spinner animation="border" variant="secondary" />
              ) : (
                t("policeFeedback")
              )}
            </button>
            <input
              type="file"
              accept=".xlsx, .xls"
              ref={fileInput}
              style={{ display: "none" }}
              onChange={handleUploadFile}
            />
          </div>
        ) : (
          <div className="buttons">
            <button
              type="button"
              className="downloadButton"
              onClick={() => setModalMassLoadingShow(true)}
            >
              <FileEarmarkSpreadsheet size={24} />
              {t("massLoading")}
            </button>
            <button
              type="button"
              className="downloadButton"
              onClick={handleExportInformationLetterReports}
              disabled={isLoadingExportInformationLetter}
            >
              {isLoadingExportInformationLetter ? (
                <Spinner animation="border" variant="secondary" />
              ) : (
                <>
                  <Envelope size={24} />
                  {t("exportInformationLetter")}
                </>
              )}
            </button>
            <div>
              <button
                type="button"
                className="downloadButton"
                onClick={() => setShowConfirmationModal(true)}
                disabled={isSendToPoliceBlocked ? true : isLoadingSendToPolice}
              >
                {isLoadingSendToPolice ? (
                  <Spinner animation="border" variant="secondary" />
                ) : (
                  <>
                    <Send size={24} />
                    {t("sendToPolice")}
                  </>
                )}
              </button>
              {isSendToPoliceBlocked ? (
                <div className="error-message">
                  {" "}
                  {t("policeProcessBlockedMessage")}
                  <br />
                  {t("waitProcessToStop")}
                </div>
              ) : null}
            </div>
          </div>
        ))}
      <ModalBootstrap
        onHide={() => setShowConfirmationModal(false)}
        show={showConfirmationModal}
        closeButtonText={t("cancel")}
        title={t("confirmExportReport")}
        icon={<Send size={24} />}
      >
        <div className="reprter-information">
          <p>{t("completeAction")}</p>
          <p>{t("popupSendToPoliceFinalAlert")}</p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => handleConfirmSendToPolice()}
        >
          {t("confirmation")}
        </button>
      </ModalBootstrap>
      <ModalBootstrap
        onHide={() => setModalShow(false)}
        show={modalShow}
        closeButtonText={t("gotIt")}
        title={t("policeFeedbackSuccess")}
        icon={<CheckCircle size={24} />}
      >
        {getModalChildren()}
      </ModalBootstrap>
      {modalMassLoadingShow && (
        <MassLoading handleClose={() => setModalMassLoadingShow(false)} />
      )}
    </div>
  );
};

export default Title;
